import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router";
import { ArrowLeft, CheckCircle2, ExternalLink } from "lucide-react";
import { useQuizDetail } from "~/api/v2/useQuizDetail";
import { useUserContext } from "~/hooks/useUserContext";
import { canManageQuizzes } from "~/helpers/quizPermissions";
import { Button } from "~/atoms/button";
import { CenterSpinner } from "~/atoms/spinner";
import HeadingPage from "~/molecules/HeadingPage";
import QuizEditor from "~/organisms/anchoring/QuizEditor";
import QuizTaker from "~/organisms/anchoring/QuizTaker";

type QuizMode = "take" | "edit";

const QuizPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useUserContext();
  const quizId = Number(id);
  const { data: quiz, error, isLoading, mutate } = useQuizDetail(quizId);

  const canEdit = canManageQuizzes(user.role);

  // Invited managers can both take and edit; taking is the default when the
  // user was invited, and editing is offered as a toggle. `?mode=edit` lands a
  // manager straight in edit mode (e.g. from the anchored documents table).
  const isInvited = quiz?.invited ?? false;
  const canToggle = canEdit && isInvited;
  const [preferredMode, setPreferredMode] = useState<QuizMode | null>(
    searchParams.get("mode") === "edit" && canEdit ? "edit" : null,
  );
  const mode: QuizMode = preferredMode ?? (isInvited ? "take" : "edit");

  if (isLoading) return <CenterSpinner />;

  return (
    <div className="container space-y-6 py-7">
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground self-start"
          asChild
        >
          <Link to="/anchoring/me">
            <ArrowLeft className="size-4" />
            {t(($) => $.anchoring.quiz.back)}
          </Link>
        </Button>
        {quiz && (
          <div className="flex items-center justify-between gap-4">
            <HeadingPage title={quiz.title} />
            <a
              href={`/documents/${quiz.documentId}?versionId=${quiz.documentVersionId}`}
              target="_blank"
              rel="noreferrer"
              className="text-muted-foreground hover:text-foreground flex shrink-0 items-center gap-1.5 text-sm"
            >
              {t(($) => $.anchoring.quiz.viewDocument)}
              <ExternalLink className="size-4" />
            </a>
          </div>
        )}
      </div>

      {error || !quiz ? (
        <p className="text-muted-foreground">
          {t(($) => $.anchoring.quiz.notFound)}
        </p>
      ) : !isInvited && !canEdit ? (
        <p className="text-muted-foreground">
          {t(($) => $.anchoring.quiz.noAccess)}
        </p>
      ) : (
        <div className="space-y-6">
          {canToggle && (
            <div className="flex gap-2">
              <Button
                variant={mode === "take" ? "default" : "outline"}
                size="sm"
                onClick={() => setPreferredMode("take")}
              >
                {t(($) => $.anchoring.quiz.takeQuiz)}
              </Button>
              <Button
                variant={mode === "edit" ? "default" : "outline"}
                size="sm"
                onClick={() => setPreferredMode("edit")}
              >
                {t(($) => $.anchoring.manage.edit)}
              </Button>
            </div>
          )}

          {mode === "edit" ? (
            <QuizEditor quiz={quiz} onSaved={() => void mutate()} />
          ) : quiz.completed ? (
            <div className="flex justify-center">
              <div className="flex max-w-lg flex-col items-center gap-4 py-10 text-center">
                <CheckCircle2 className="text-success size-12" />
                <h2 className="text-xl font-semibold">
                  {t(($) => $.anchoring.quiz.take.alreadyCompletedTitle)}
                </h2>
                <p className="text-muted-foreground">
                  {t(($) => $.anchoring.quiz.take.alreadyCompletedDescription)}
                </p>
              </div>
            </div>
          ) : (
            <QuizTaker quiz={quiz} onCompleted={() => void mutate()} />
          )}
        </div>
      )}
    </div>
  );
};

export default QuizPage;
